import {
	SET_LOGIN_CODE_OPTIONS,
	SET_SEND_CODE_RESULT,
	SET_VALIDATE_LOGIN_CODE_RESULT,
	SET_REMEMBER_DEVICE,
	SET_SEND_RESET_PASSWORD_LINK_RESULT,
	SET_VALIDATE_PASSWORD_RESET_CODE_RESULT,
	SET_RESET_PASSWORD_RESULT
} from './login-mutation-types';
import apiService from '@/services/api-service';
import cookies from 'vue-cookies';
import buildRoute from '@/composables/build-route';
import { differenceInDays } from 'date-fns';

const rememberDeviceCookieName = 'vue2rd';

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'login',
	namespaced: true,
	state: {
		loginCodeOptions: {},
		sendLoginCodeResult: {},
		validateLoginCodeResult: {},
		sendResetPasswordLinkResult: {},
		validatePasswordResetCodeResult: {},
		resetPasswordResult: {},
		rememberDevice: false,
		rememberDeviceCookieName: rememberDeviceCookieName,
		rememberDeviceHash: cookies.get(rememberDeviceCookieName)
	},
	mutations: {
		[SET_LOGIN_CODE_OPTIONS](state, loginCodeOptions) {
			state.loginCodeOptions = loginCodeOptions;
		},
		[SET_SEND_CODE_RESULT](state, sendLoginCodeResult) {
			state.sendLoginCodeResult = sendLoginCodeResult;
		},
		[SET_VALIDATE_LOGIN_CODE_RESULT](state, validateLoginCodeResult) {
			state.validateLoginCodeResult = validateLoginCodeResult;
		},
		[SET_REMEMBER_DEVICE](state, rememberDevice) {
			state.rememberDevice = rememberDevice;

			state.rememberDeviceHash =
				state.validateLoginCodeResult.rememberDeviceHash;

			if (state.rememberDevice) {
				let expirationDate = new Date(
					state.validateLoginCodeResult.expirationDate
				);

				//note that the cookie expiration doesn't matter,
				//the expiration date is built into the token
				//add a day to ensure the cookie doesn't expire before the token
				let rememberDays =
					differenceInDays(expirationDate, new Date()) + 1;
				let cookieDomain =
					'.' +
					window.location.hostname
						.split('.')
						.slice(1)
						.join('.');
				cookies.set(
					state.rememberDeviceCookieName,
					state.validateLoginCodeResult.rememberDeviceHash,
					`${rememberDays}d`,
					'/',
					cookieDomain
				);
			} else {
				cookies.remove(state.rememberDeviceCookieName);
			}
		},
		[SET_SEND_RESET_PASSWORD_LINK_RESULT](
			state,
			sendResetPasswordLinkResult
		) {
			state.sendResetPasswordLinkResult = sendResetPasswordLinkResult;
		},
		[SET_VALIDATE_PASSWORD_RESET_CODE_RESULT](
			state,
			validatePasswordResetCodeResult
		) {
			state.validatePasswordResetCodeResult = validatePasswordResetCodeResult;
		},
		[SET_RESET_PASSWORD_RESULT](state, resetPasswordResult) {
			state.resetPasswordResult = resetPasswordResult;
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch }) {
		async getLoginCodeOptionsAction({ commit }) {
			const loginCodeOptions = await apiService.get(
				'/dealership/employee/send-options'
			);

			commit(SET_LOGIN_CODE_OPTIONS, loginCodeOptions);
		},
		async sendLoginCodeAction({ commit }, sendType) {
			const sendLoginCodeResult = await apiService.post(
				'/verificationcodes/send-login-code',
				{ sendType: sendType }
			);

			commit(SET_SEND_CODE_RESULT, sendLoginCodeResult);
		},
		async validateLoginCodeAction({ dispatch }, { loginCode, userId }) {
			//when we validate the login, we haven't prompted the user
			//to select whether to remember the device,
			//but we'll still call the api setting rememberDevice = true,
			//so we'll have the hash token if the user selectes to remember
			await dispatch('validateCodeAction', {
				userId: userId,
				loginCode: loginCode,
				codeType: 'TwoFactorAuth',
				rememberDevice: true,
				mutation: SET_VALIDATE_LOGIN_CODE_RESULT
			});
		},
		async validatePasswordResetCodeAction(
			{ commit },
			{ resetCode, userId }
		) {
			const validateCodeResult = await apiService.post(
				'/verificationcodes/validate-reset',
				{
					userId: userId,
					code: resetCode,
					codeType: 'PasswordReset'
				}
			);

			commit(SET_VALIDATE_PASSWORD_RESET_CODE_RESULT, validateCodeResult);
		},
		async validateCodeAction(
			{ commit },
			{ loginCode, userId, codeType, rememberDevice, mutation }
		) {
			const validateCodeResult = await apiService.post(
				'/verificationcodes/validate',
				{
					userId: userId,
					code: loginCode,
					codeType: codeType,
					rememberDevice: rememberDevice
				}
			);

			commit(mutation, validateCodeResult);
		},
		rememberDeviceAction({ commit }, rememberDevice) {
			commit(SET_REMEMBER_DEVICE, rememberDevice);
		},
		async sendResetPasswordLinkAction({ commit }, { sentTo, returnUrl }) {
			const sendResetPasswordLinkResult = await apiService.post(
				'/verificationcodes/passwordreset/send-reset-password-link',
				{
					email: sentTo,
					returnUrl: returnUrl
				}
			);

			commit(
				SET_SEND_RESET_PASSWORD_LINK_RESULT,
				sendResetPasswordLinkResult
			);
		},
		async resetPasswordAction(
			{ commit },
			{ userId, password, verificationCode }
		) {
			const resetPasswordResult = await apiService.post(
				buildRoute('/users/{userId}/password-reset', {
					userId: userId
				}),
				{
					id: userId,
					password: password,
					verificationCode: verificationCode
				}
			);

			commit(SET_RESET_PASSWORD_RESULT, resetPasswordResult);
		}
	},
	getters: {
		loginCodeOptions: state => state.loginCodeOptions,
		loginCodeExpiresMinutes: state =>
			state.sendLoginCodeResult.expireMinutes,
		loginCodeUserId: state => state.sendLoginCodeResult.userId,
		loginCodeEmail: state => state.sendLoginCodeResult.sentTo,
		loginCodePhoneNumber: state => state.sendLoginCodeResult.sentTo,
		validateLoginCodeResult: state => state.validateLoginCodeResult,
		sendResetPasswordLinkResult: state => state,
		validatePasswordResetCodeResult: state =>
			state.validatePasswordResetCodeResult,
		validatePasswordResetCodeError: state => {
			let error;

			if (state.validatePasswordResetCodeResult) {
				if (!state.validatePasswordResetCodeResult.valid) {
					error = state.validatePasswordResetCodeResult.failureReason;
				}
			}

			return error;
		},
		resetPasswordToken: state => {
			let token;

			if (state.validatePasswordResetCodeResult) {
				token = state.validatePasswordResetCodeResult.resetToken;
			}

			return token;
		},
		resetPasswordResult: state => state.resetPasswordResult,
		loginEmail: state => {
			let emailOption;
			let email;

			if (state.loginCodeOptions && state.loginCodeOptions.items) {
				emailOption = state.loginCodeOptions.items.find(
					o => o.optionType === 'primaryEmail'
				);

				if (emailOption) {
					email = emailOption.value;
				}
			}

			return email;
		},
		resetPasswordError: state => {
			let error;

			if (
				state.resetPasswordResult &&
				!state.resetPasswordResult.success
			) {
				error = state.resetPasswordResult.failureReason;
			}

			return error;
		},
		rememberDeviceHash: state => {
			return state.rememberDeviceHash;
		}
	}
};
